/**
 * Обращение к административному набору эндпоинтов.
 *
 * Административное приложение работает только с `/api/admin/*` (п. 2.4).
 * Маркер сессии передаётся браузером в куке и коду недоступен; маркер
 * защиты от подделки межсайтовых запросов хранится в памяти модуля
 * и добавляется к изменяющим запросам (Н-303).
 *
 * Типы ответов будут заменены на генерируемые из спецификации OpenAPI
 * (АР-10, пакет @myshop/api-client); ручное описание — временное.
 */

const BASE = '/api/admin'

let csrfToken: string | null = null

export function setCsrfToken(token: string | null) {
  csrfToken = token
}

export class ApiError extends Error {
  readonly status: number
  readonly retryAfter?: number
  readonly details: ErrorDetails

  constructor(status: number, message: string, retryAfter?: number, details: ErrorDetails = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.retryAfter = retryAfter
    this.details = details
  }

  /** Сессия отсутствует или истекла. */
  get unauthorized() {
    return this.status === 401
  }
}

type Options = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
}

/** Подробности отказа, которые обработчик возвращает вместе с ошибкой. */
export type ErrorDetails = Record<string, unknown>

/**
 * Что делать, когда сервер отвечает «требуется вход».
 *
 * Задаётся приложением один раз при запуске: слой обращений к серверу
 * не знает ни о состоянии входа, ни о переходах между экранами.
 */
let onUnauthorized: (() => void) | null = null

export function setUnauthorizedHandler(fn: (() => void) | null) {
  onUnauthorized = fn
}

async function request<T>(path: string, options: Options = {}): Promise<T> {
  const method = options.method ?? 'GET'
  const headers: Record<string, string> = {}

  // Составное тело браузер собирает сам: он проставляет разделитель
  // частей, и заданный вручную Content-Type сделал бы тело неразбираемым.
  const isForm = options.body instanceof FormData
  if (options.body !== undefined && !isForm) {
    headers['Content-Type'] = 'application/json'
  }
  if (method !== 'GET' && csrfToken) {
    headers['X-CSRF-Token'] = csrfToken
  }

  const res = await fetch(BASE + path, {
    method,
    headers,
    // Кука сессии помечена HttpOnly, поэтому передаётся браузером,
    // а не кодом страницы.
    credentials: 'same-origin',
    body:
      options.body === undefined
        ? undefined
        : isForm
          ? (options.body as FormData)
          : JSON.stringify(options.body),
    signal: options.signal,
  })

  if (res.status === 204) {
    return undefined as T
  }

  let payload: unknown = null
  try {
    payload = await res.json()
  } catch {
    // тело отсутствует либо не является JSON
  }

  if (!res.ok) {
    const body = (payload ?? {}) as { error?: string; retry_after?: number }

    // Истёкшая сессия — не ошибка экрана, а состояние приложения.
    // Показывать «требуется вход» посреди перечня компаний бессмысленно:
    // сделать оттуда всё равно ничего нельзя. Приложение узнаёт об этом
    // здесь, один раз на все экраны, и переводит на вход.
    //
    // Сам вход исключён: неверный пароль отвечает тем же кодом, и его
    // ошибку обязана показать форма, а не проглотить эта проверка.
    if (res.status === 401 && !path.startsWith('/auth/login')) {
      onUnauthorized?.()
    }

    throw new ApiError(
      res.status,
      body.error ?? `Запрос завершился с кодом ${res.status}`,
      body.retry_after,
      body as ErrorDetails,
    )
  }

  return payload as T
}

// --- Типы ответов -----------------------------------------------------------

export type Company = {
  id: number
  slug: string
  name: string
  status: 'active' | 'suspended' | 'archived'
}

export type Permissions = {
  read: Record<string, boolean>
  write: Record<string, boolean>
}

export type Me = {
  id: number
  email: string
  full_name: string
  role: 'superadmin' | 'admin' | 'content_manager' | 'storekeeper'
  role_title: string
  is_platform: boolean
  must_change_password: boolean
  company: Company | null
  csrf_token: string
  expires_at: string
  permissions: Permissions
}

export type CompanyRow = {
  id: number
  slug: string
  name: string
  brand: string
  status: string
  search_alias: string
  users: number
  domains: number
  primary_host: string
  created_at: string
}

export type CategoryNode = {
  id: number
  parent_id: number | null
  code: string
  name_ru: string
  name_kk: string | null
  description_ru: string | null
  description_kk: string | null
  position: number
  is_active: boolean
  depth: number
  products_direct: number
  products_total: number
  children?: CategoryNode[]
}

export type CategoryInput = {
  parent_id?: number | null
  code?: string
  name_ru: string
  name_kk?: string | null
  description_ru?: string | null
  description_kk?: string | null
  is_active?: boolean
}

export type Attribute = {
  id: number
  code: string
  name_ru: string
  name_kk: string | null
  value_type: 'text' | 'number' | 'boolean' | 'list'
  value_type_title: string
  unit: string | null
  in_facets: boolean
  show_in_card: boolean
  position: number
  categories: number
  products: number
}

export type AttributeInput = {
  code?: string
  name_ru: string
  name_kk?: string | null
  value_type?: string
  unit?: string | null
  in_facets: boolean
  show_in_card?: boolean
}

export type CategoryAttribute = {
  attribute_id: number
  code: string
  name_ru: string
  value_type: string
  unit: string | null
  in_facets: boolean
  show_in_card: boolean
  is_required: boolean
  position: number
  inherited: boolean
  owner_category_id: number
  owner_category_name: string
}

export type ProductRow = {
  id: number
  sku: string
  name_ru: string
  category_id: number
  category_name: string
  brand: string | null
  price: number
  stock_total: number
  is_published: boolean
  image_key: string | null
  image_count: number
  missing_required: number
  updated_at: string
}

export type ProductFilter = {
  q?: string
  category_id?: number | null
  published?: boolean | null
  in_stock?: boolean | null
  has_images?: boolean | null
  attributes_complete?: boolean | null
  brand?: string | null
  sort?: string
  desc?: boolean
  page?: number
  limit?: number
}

export type PublishBlocker = { field: string; message: string }
export type Readiness = { ready: boolean; blockers: PublishBlocker[] | null }

export type CardAttribute = {
  attribute_id: number
  code: string
  name_ru: string
  unit: string | null
  value_type: string
  is_required: boolean
  inherited: boolean
  owner_category_name: string
  values: string[]
}

export type ProductImage = {
  id: number
  file_key: string
  position: number
  alt_text: string | null
}

export type StockRow = {
  warehouse_id: number
  warehouse_name: string
  is_public: boolean
  quantity: number
}

export type ProductCard = {
  id: number
  sku: string
  category_id: number
  name_ru: string
  name_kk: string | null
  description_ru: string | null
  description_kk: string | null
  brand: string | null
  barcode: string | null
  price: number
  unit: string
  sale_multiple: number
  is_published: boolean
  slug: string
  updated_at: string
  attributes: CardAttribute[]
  orphaned: { attribute_id: number; name_ru: string; value: string }[]
  images: ProductImage[]
  stock: StockRow[]
  readiness: Readiness
}

export type ProductInput = {
  sku: string
  category_id: number
  name_ru: string
  name_kk?: string | null
  description_ru?: string | null
  brand?: string | null
  barcode?: string | null
  price: number
  unit?: string
  sale_multiple?: number
  slug?: string
  attributes?: Record<string, string[]>
}


// --- Импорт товаров (п. 4.17) -------------------------------------------------

export type ImportSource = { code: string; name: string; hint: string; ready: boolean }

export type ImportRun = {
  id: number
  source: string
  source_ref: string
  kind: string
  state: 'pending' | 'preview' | 'running' | 'done' | 'failed' | 'cancelled'
  file_name: string | null
  total_rows: number
  created_rows: number
  updated_rows: number
  failed_rows: number
  progress: number
  progress_note: string
  created_at: string
  finished_at: string | null
}

export type ImportSuggestion = { id: number; name: string; score: number }
export type ImportDecision = 'create' | 'map' | 'skip'

export type ImportCategoryMatch = {
  source_path: string
  products: number
  decision: ImportDecision
  category_id: number | null
  suggested: ImportSuggestion | null
  saved: boolean
}

export type ImportAttributeMatch = {
  source_name: string
  products: number
  is_list: boolean
  values: number
  decision: ImportDecision
  attribute_id: number | null
  suggested: ImportSuggestion | null
  saved: boolean
}

export type ImportPreview = {
  total: number
  create: number
  update: number
  failed: number
  rows: {
    row: number
    action: string
    sku: string
    title: string
    category: string
    price: number
    stock: number
    images: number
    attributes: number
  }[]
  errors: { row: number; sku?: string; field: string; message: string }[]
  categories: ImportCategoryMatch[]
  attributes: ImportAttributeMatch[]
}


// --- Управление поисковым индексом (п. 4.18) ----------------------------------

export type QueueDepth = { name: string; messages: number; consumers: number; dead: boolean }

export type IndexStatus = {
  alias: string
  pending: number
  oldest_age_seconds: number | null
  failed: number
  last_published_at: string | null
  publishable: number
  indexed: number
  mismatch: boolean
  queues: QueueDepth[]
  broker_error: string
}

export type StuckRecord = {
  id: number
  entity_type: string
  entity_id: number
  operation: string
  routing_key: string
  attempts: number
  last_error: string | null
  created_at: string
  title: string | null
}

export type DeadMessage = {
  queue: string
  routing_key: string
  body: string
  reason: string
  attempts: number
  timestamp: string
}


export type StockImportRow = {
  row: number
  sku: string
  warehouse: string
  quantity: number
  current: number
  delta: number
  title: string
}

export type StockImportPreview = {
  total: number
  ready: number
  failed: number
  noop: number
  changed: number
  mode: 'set' | 'add'
  rows: StockImportRow[]
  errors: { row: number; sku?: string; field: string; message: string }[]
}


// --- Касса: заказы и покупатели -----------------------------------------------

export type Customer = {
  id: number
  name: string
  phone: string | null
  email: string | null
  comment: string | null
  external_source: string | null
  external_id: string | null
  created_at: string
  orders: number
}

export type OrderLine = {
  product_id: number
  sku: string
  name: string
  external_code: string
  external_name: string
  quantity: number
  price: number
  sum: number
  available: number
}

export type Order = {
  id: number
  number: string
  customer_id: number | null
  customer_name: string | null
  warehouse_id: number
  warehouse_name: string
  source: 'pos' | 'external'
  state: 'draft' | 'confirmed' | 'cancelled'
  external_source: string | null
  external_id: string | null
  external_ref: string | null
  channel_id: number | null
  channel_name: string | null
  external_status: string | null
  external_state: string | null
  waybill_url: string | null
  waybill_number: string | null
  delivery_type: string | null
  delivery_address: string | null
  synced_at: string | null
  comment: string | null
  created_at: string
  confirmed_at: string | null
  cancelled_at: string | null
  lines: OrderLine[]
  total: number
  positions: number
  units: number
}


// --- Мои магазины: каналы продаж ----------------------------------------------

export type ChannelKind = { kind: string; name: string; hint: string; ready: boolean }

export type Channel = {
  id: number
  kind: string
  name: string
  has_token: boolean
  token_hint: string
  warehouse_id: number | null
  warehouse_name: string | null
  state: 'draft' | 'active' | 'error' | 'disabled'
  merchant_id: string | null
  last_check_at: string | null
  last_error: string | null
  orders_synced_to: string | null
  orders: number
  created_at: string
}

function filterQuery(f: ProductFilter): string {
  const p = new URLSearchParams()
  const put = (k: string, v: unknown) => {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v))
  }
  put('q', f.q)
  put('category_id', f.category_id)
  put('brand', f.brand)
  put('published', f.published)
  put('in_stock', f.in_stock)
  put('has_images', f.has_images)
  put('attributes_complete', f.attributes_complete)
  put('sort', f.sort)
  put('desc', f.desc)
  put('page', f.page)
  put('limit', f.limit)
  const s = p.toString()
  return s ? `?${s}` : ''
}

export type Warehouse = {
  id: number
  code: string
  name: string
  address: string | null
  is_public: boolean
  is_active: boolean
  positions: number
  units: number
  value: number
}

export type LookupItem = {
  product_id: number
  sku: string
  name: string
  barcode: string
  price: number
  stock: number
  exact: boolean
}

export type Movement = {
  id: number
  created_at: string
  product_id: number
  product_sku: string
  product_name: string
  warehouse_name: string
  reason: string
  reason_name: string
  delta: number
  comment: string
  user_name: string
  document_type: string
  document_id: number | null
}

export type Reason = {
  code: string
  name_ru: string
  sign: number
  requires_comment: boolean
}

export type DocLine = {
  id: number
  product_id: number
  product_sku: string
  product_name: string
  quantity: number
}

export type Receipt = {
  id: number
  number: string
  warehouse_id: number
  warehouse_name: string
  supplier: string | null
  comment: string | null
  state: string
  state_title: string
  editable: boolean
  created_at: string
  posted_at: string | null
  lines: DocLine[] | null
  line_count: number
}

export type Transfer = {
  id: number
  number: string
  source_warehouse_id: number
  source_warehouse_name: string
  target_warehouse_id: number
  target_warehouse_name: string
  comment: string | null
  state: string
  state_title: string
  editable: boolean
  created_at: string
  posted_at: string | null
  lines: DocLine[] | null
  line_count: number
}

export type InventoryLine = {
  product_id: number
  product_sku: string
  product_name: string
  snapshot_quantity: number
  movements_since: number
  expected: number
  actual_quantity: number | null
  variance: number | null
  price: number
}

export type InventorySummary = {
  total: number
  filled: number
  variances: number
  surplus: number
  shortage: number
  value_diff: number
}

export type Inventory = {
  id: number
  number: string
  warehouse_id: number
  warehouse_name: string
  scope: string
  state: string
  state_title: string
  editable: boolean
  started_at: string
  posted_at: string | null
  lines: InventoryLine[] | null
  line_count: number
  summary: InventorySummary
}

// --- Методы ------------------------------------------------------------------

export const api = {
  async login(email: string, password: string): Promise<Me> {
    const me = await request<Me>('/auth/login', {
      method: 'POST',
      body: { email, password },
    })
    setCsrfToken(me.csrf_token)
    return me
  },

  async me(): Promise<Me> {
    const me = await request<Me>('/auth/me')
    setCsrfToken(me.csrf_token)
    return me
  },

  async logout(): Promise<void> {
    await request<void>('/auth/logout', { method: 'POST' })
    setCsrfToken(null)
  },

  async companies(): Promise<CompanyRow[]> {
    const res = await request<{ companies: CompanyRow[] }>('/platform/companies')
    return res.companies ?? []
  },

  createCompany(body: {
    slug: string
    name: string
    brand?: string
    host?: string
  }): Promise<{ company: CompanyRow; warning?: string }> {
    return request('/platform/companies', { method: 'POST', body })
  },

  setCompanyBrand(id: number, brand: string): Promise<{ brand: string; shop_title: string }> {
    return request(`/platform/companies/${id}/brand`, { method: 'PUT', body: { brand } })
  },

  // --- Категории (п. 4.3) ---------------------------------------------------

  async categoryTree(): Promise<CategoryNode[]> {
    const res = await request<{ categories: CategoryNode[] }>('/categories')
    return res.categories ?? []
  },

  createCategory(body: CategoryInput): Promise<CategoryNode> {
    return request<CategoryNode>('/categories', { method: 'POST', body })
  },

  updateCategory(id: number, body: CategoryInput): Promise<CategoryNode> {
    return request<CategoryNode>(`/categories/${id}`, { method: 'PUT', body })
  },

  deleteCategory(id: number): Promise<void> {
    return request<void>(`/categories/${id}`, { method: 'DELETE' })
  },

  moveCategory(id: number, parentId: number | null, position: number): Promise<void> {
    return request<void>(`/categories/${id}/move`, {
      method: 'POST',
      body: { parent_id: parentId, position },
    })
  },

  reorderCategories(ids: number[]): Promise<void> {
    return request<void>('/categories/reorder', { method: 'POST', body: { ids } })
  },

  // --- Набор атрибутов категории (п. 4.5) -------------------------------------

  async categoryAttributes(id: number): Promise<CategoryAttribute[]> {
    const res = await request<{ attributes: CategoryAttribute[] }>(`/categories/${id}/attributes`)
    return res.attributes ?? []
  },

  bindAttributes(id: number, attributeIds: number[], isRequired: boolean): Promise<void> {
    return request<void>(`/categories/${id}/attributes`, {
      method: 'POST',
      body: { attribute_ids: attributeIds, is_required: isRequired },
    })
  },

  unbindAttribute(categoryId: number, attributeId: number): Promise<void> {
    return request<void>(`/categories/${categoryId}/attributes/${attributeId}`, {
      method: 'DELETE',
    })
  },

  setAttributeRequired(
    categoryId: number,
    attributeId: number,
    isRequired: boolean,
  ): Promise<{ status: string; missing_required?: number }> {
    return request(`/categories/${categoryId}/attributes/${attributeId}/required`, {
      method: 'PUT',
      body: { is_required: isRequired },
    })
  },

  reorderCategoryAttributes(categoryId: number, ids: number[]): Promise<void> {
    return request<void>(`/categories/${categoryId}/attributes/reorder`, {
      method: 'POST',
      body: { ids },
    })
  },

  copyAttributes(toCategoryId: number, fromCategoryId: number): Promise<{ added: number }> {
    return request(`/categories/${toCategoryId}/attributes/copy`, {
      method: 'POST',
      body: { from_category_id: fromCategoryId },
    })
  },


  // --- Импорт товаров (п. 4.17) -----------------------------------------------

  async importSources(): Promise<ImportSource[]> {
    const res = await request<{ sources: ImportSource[] }>('/imports/sources')
    return res.sources ?? []
  },

  async imports(): Promise<ImportRun[]> {
    const res = await request<{ imports: ImportRun[] }>('/imports')
    return res.imports ?? []
  },

  importRun(id: number): Promise<ImportRun> {
    return request<ImportRun>(`/imports/${id}`)
  },

  startImport(source: string, ref: string): Promise<ImportRun> {
    return request<ImportRun>('/imports', { method: 'POST', body: { source, ref } })
  },

  async uploadImport(file: File): Promise<ImportRun> {
    const form = new FormData()
    form.append('file', file)
    return request<ImportRun>('/imports/upload', { method: 'POST', body: form })
  },

  importPreview(id: number): Promise<ImportPreview> {
    return request<ImportPreview>(`/imports/${id}/preview`)
  },

  applyImport(
    id: number,
    categories: ImportCategoryMatch[],
    attributes: ImportAttributeMatch[],
  ): Promise<void> {
    return request<void>(`/imports/${id}/apply`, {
      method: 'POST',
      body: { categories, attributes },
    })
  },

  cancelImport(id: number): Promise<void> {
    return request<void>(`/imports/${id}/cancel`, { method: 'POST' })
  },



  async uploadStock(
    file: File,
    mode: 'set' | 'add',
  ): Promise<{ import: ImportRun; preview: StockImportPreview }> {
    const form = new FormData()
    form.append('file', file)
    form.append('mode', mode)
    return request('/imports/stock', { method: 'POST', body: form })
  },

  applyStock(
    id: number,
    mode: 'set' | 'add',
  ): Promise<{ applied: number; unchanged: number; failed: number }> {
    return request(`/imports/${id}/apply-stock`, { method: 'POST', body: { mode } })
  },



  // --- Мои магазины: каналы продаж --------------------------------------------

  channelKinds(): Promise<{ kinds: ChannelKind[]; secrets_ready: boolean }> {
    return request('/channels/kinds')
  },

  async channels(): Promise<Channel[]> {
    const res = await request<{ channels: Channel[] }>('/channels')
    return res.channels ?? []
  },

  createChannel(body: {
    kind: string
    name: string
    token: string
    merchant_id?: string | null
    warehouse_id?: number | null
  }): Promise<Channel> {
    return request<Channel>('/channels', { method: 'POST', body })
  },

  updateChannel(
    id: number,
    body: {
      name: string
      token?: string
      merchant_id?: string | null
      warehouse_id?: number | null
    },
  ): Promise<Channel> {
    return request<Channel>(`/channels/${id}`, { method: 'PUT', body })
  },

  checkChannel(id: number): Promise<Channel> {
    return request<Channel>(`/channels/${id}/check`, { method: 'POST' })
  },

  enableChannel(id: number, enabled: boolean): Promise<Channel> {
    return request<Channel>(`/channels/${id}/enabled`, { method: 'POST', body: { enabled } })
  },

  deleteChannel(id: number): Promise<void> {
    return request<void>(`/channels/${id}`, { method: 'DELETE' })
  },

  // --- Касса: заказы и покупатели ---------------------------------------------

  async orders(state = ''): Promise<Order[]> {
    const q = state ? `?state=${state}` : ''
    const res = await request<{ orders: Order[] }>(`/orders${q}`)
    return res.orders ?? []
  },

  order(id: number): Promise<Order> {
    return request<Order>(`/orders/${id}`)
  },

  createOrder(body: {
    warehouse_id: number
    customer_id?: number | null
    comment?: string | null
  }): Promise<Order> {
    return request<Order>('/orders', { method: 'POST', body })
  },

  setOrderLine(id: number, productId: number, quantity: number, price?: number): Promise<Order> {
    return request<Order>(`/orders/${id}/line`, {
      method: 'POST',
      body: { product_id: productId, quantity, price },
    })
  },

  setOrderCustomer(id: number, customerId: number | null): Promise<Order> {
    return request<Order>(`/orders/${id}/customer`, {
      method: 'POST',
      body: { customer_id: customerId },
    })
  },

  setOrderComment(id: number, comment: string | null): Promise<Order> {
    return request<Order>(`/orders/${id}/comment`, { method: 'POST', body: { comment } })
  },

  confirmOrder(id: number): Promise<Order> {
    return request<Order>(`/orders/${id}/confirm`, { method: 'POST' })
  },

  cancelOrder(id: number): Promise<Order> {
    return request<Order>(`/orders/${id}/cancel`, { method: 'POST' })
  },

  deleteOrder(id: number): Promise<void> {
    return request<void>(`/orders/${id}`, { method: 'DELETE' })
  },

  async customers(query = ''): Promise<Customer[]> {
    const q = query ? `?q=${encodeURIComponent(query)}` : ''
    const res = await request<{ customers: Customer[] }>(`/customers${q}`)
    return res.customers ?? []
  },

  createCustomer(body: {
    name: string
    phone?: string | null
    email?: string | null
    comment?: string | null
  }): Promise<Customer> {
    return request<Customer>('/customers', { method: 'POST', body })
  },


  syncChannel(id: number): Promise<{ fetched: number; created: number; updated: number; unmatched: number }> {
    return request(`/channels/${id}/sync`, { method: 'POST' })
  },

  acceptOrder(id: number): Promise<Order> {
    return request<Order>(`/orders/${id}/accept`, { method: 'POST' })
  },

  assembleOrder(id: number, places: number): Promise<Order> {
    return request<Order>(`/orders/${id}/assemble`, { method: 'POST', body: { places } })
  },

  completeOrder(id: number): Promise<Order> {
    return request<Order>(`/orders/${id}/complete`, { method: 'POST' })
  },

  rejectOrder(id: number, reason: string, comment?: string): Promise<Order> {
    return request<Order>(`/orders/${id}/reject`, { method: 'POST', body: { reason, comment } })
  },

  matchMarketplaceProduct(body: {
    kind: string
    code: string
    product_id: number
    external_name?: string
  }): Promise<{ lines_updated: number }> {
    return request('/marketplace/match', { method: 'POST', body })
  },

  // --- Управление поисковым индексом (п. 4.18) --------------------------------

  indexStatus(): Promise<IndexStatus> {
    return request<IndexStatus>('/index/status')
  },

  reindexAll(): Promise<{ queued: number }> {
    return request('/index/reindex', { method: 'POST' })
  },

  reindexCategory(id: number): Promise<{ queued: number }> {
    return request(`/index/reindex/${id}`, { method: 'POST' })
  },

  reindexProduct(id: number): Promise<{ queued: number }> {
    return request(`/index/reindex/product/${id}`, { method: 'POST' })
  },

  async stuckRecords(): Promise<StuckRecord[]> {
    const res = await request<{ records: StuckRecord[] }>('/index/stuck')
    return res.records ?? []
  },

  retryStuck(ids: number[] = []): Promise<{ retried: number }> {
    return request('/index/stuck/retry', { method: 'POST', body: { ids } })
  },

  async deadLetters(queue: string): Promise<DeadMessage[]> {
    const res = await request<{ messages: DeadMessage[] }>(
      `/index/dead?queue=${encodeURIComponent(queue)}`,
    )
    return res.messages ?? []
  },

  requeueDead(queue: string): Promise<{ requeued: number; skipped: number; reason?: string }> {
    return request('/index/dead/requeue', { method: 'POST', body: { queue } })
  },

  purgeDead(queue: string): Promise<{ purged: number }> {
    return request('/index/dead/purge', { method: 'POST', body: { queue } })
  },

  // --- Справочник атрибутов (п. 4.4) ------------------------------------------

  async attributes(query = ''): Promise<Attribute[]> {
    const q = query ? `?q=${encodeURIComponent(query)}` : ''
    const res = await request<{ attributes: Attribute[] }>(`/attributes${q}`)
    return res.attributes ?? []
  },

  createAttribute(body: AttributeInput): Promise<Attribute> {
    return request<Attribute>('/attributes', { method: 'POST', body })
  },

  updateAttribute(
    id: number,
    body: AttributeInput,
  ): Promise<{ attribute: Attribute; reindex_required?: boolean; warning?: string }> {
    return request(`/attributes/${id}`, { method: 'PUT', body })
  },

  deleteAttribute(id: number): Promise<void> {
    return request<void>(`/attributes/${id}`, { method: 'DELETE' })
  },

  async attributeCategories(id: number): Promise<{ id: number; name_ru: string; code: string }[]> {
    const res = await request<{ categories: { id: number; name_ru: string; code: string }[] }>(
      `/attributes/${id}/categories`,
    )
    return res.categories ?? []
  },

  // --- Товары (пп. 4.6, 4.7) --------------------------------------------------

  products(f: ProductFilter): Promise<{
    products: ProductRow[]
    total: number
    page: number
    limit: number
  }> {
    return request(`/products${filterQuery(f)}`)
  },

  async productBrands(): Promise<string[]> {
    const res = await request<{ brands: string[] | null }>('/products/filters')
    return res.brands ?? []
  },

  product(id: number): Promise<ProductCard> {
    return request<ProductCard>(`/products/${id}`)
  },

  createProduct(body: ProductInput): Promise<ProductCard> {
    return request<ProductCard>('/products', { method: 'POST', body })
  },

  updateProduct(id: number, body: ProductInput): Promise<ProductCard> {
    return request<ProductCard>(`/products/${id}`, { method: 'PUT', body })
  },

  deleteProduct(id: number): Promise<void> {
    return request<void>(`/products/${id}`, { method: 'DELETE' })
  },

  setPublished(id: number, isPublished: boolean): Promise<{ status: string }> {
    return request(`/products/${id}/published`, {
      method: 'PUT',
      body: { is_published: isPublished },
    })
  },

  previewCategoryChange(
    id: number,
    categoryId: number,
  ): Promise<{ dropped: { attribute_id: number; name_ru: string; value: string }[] }> {
    return request(`/products/${id}/category-change?category_id=${categoryId}`)
  },

  changeProductCategory(id: number, categoryId: number, keepValues: boolean): Promise<ProductCard> {
    return request<ProductCard>(`/products/${id}/category-change`, {
      method: 'POST',
      body: { category_id: categoryId, keep_values: keepValues },
    })
  },

  bulkProducts(
    body: Record<string, unknown>,
    filter?: ProductFilter,
  ): Promise<{ status: string; affected: number; skipped?: number[]; note?: string }> {
    return request(`/products/bulk${filter ? filterQuery(filter) : ''}`, {
      method: 'POST',
      body,
    })
  },

  // --- Изображения (п. 4.8) ---------------------------------------------------

  async uploadImages(
    productId: number,
    files: File[],
  ): Promise<{ images: ProductImage[]; failed: { name: string; error: string }[] | null }> {
    const form = new FormData()
    files.forEach((f) => form.append('files', f))

    const res = await fetch(`${BASE}/products/${productId}/images`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : {},
      body: form,
    })
    const payload = await res.json().catch(() => ({}))
    if (!res.ok) {
      const b = payload as { error?: string }
      throw new ApiError(res.status, b.error ?? 'Не удалось загрузить файлы', undefined, payload)
    }
    return payload
  },

  reorderImages(productId: number, ids: number[]): Promise<void> {
    return request<void>(`/products/${productId}/images/reorder`, {
      method: 'POST',
      body: { ids },
    })
  },

  deleteImage(imageId: number): Promise<void> {
    return request<void>(`/images/${imageId}`, { method: 'DELETE' })
  },

  setImageAlt(imageId: number, altText: string): Promise<void> {
    return request<void>(`/images/${imageId}/alt`, { method: 'PUT', body: { alt_text: altText } })
  },

  // --- Склады (п. 4.9) --------------------------------------------------------

  async warehouses(): Promise<Warehouse[]> {
    const res = await request<{ warehouses: Warehouse[] | null }>('/warehouses')
    return res.warehouses ?? []
  },

  createWarehouse(body: Record<string, unknown>): Promise<Warehouse> {
    return request<Warehouse>('/warehouses', { method: 'POST', body })
  },

  updateWarehouse(id: number, body: Record<string, unknown>): Promise<Warehouse> {
    return request<Warehouse>(`/warehouses/${id}`, { method: 'PUT', body })
  },

  // --- Операции склада --------------------------------------------------------

  async stockLookup(q: string, warehouseId?: number): Promise<LookupItem[]> {
    const p = new URLSearchParams({ q })
    if (warehouseId) p.set('warehouse_id', String(warehouseId))
    const res = await request<{ items: LookupItem[] | null }>(`/stock/lookup?${p}`)
    return res.items ?? []
  },

  adjustStock(body: {
    product_id: number
    warehouse_id: number
    target: number
    comment: string
  }): Promise<{ before: number; after: number; delta: number }> {
    return request('/stock/adjust', { method: 'POST', body })
  },

  writeOff(body: {
    product_id: number
    warehouse_id: number
    quantity: number
    cause: string
    comment?: string
  }): Promise<{ status: string }> {
    return request('/stock/write-off', { method: 'POST', body })
  },

  sell(body: { product_id: number; warehouse_id: number; quantity: number }): Promise<{
    movement_id: number
    undo_seconds: number
  }> {
    return request('/stock/sell', { method: 'POST', body })
  },

  undoSale(movementId: number): Promise<{ status: string }> {
    return request(`/stock/sell/${movementId}/undo`, { method: 'POST', body: {} })
  },

  async recentSales(warehouseId: number): Promise<Movement[]> {
    const res = await request<{ movements: Movement[] | null }>(
      `/stock/recent-sales?warehouse_id=${warehouseId}`,
    )
    return res.movements ?? []
  },

  reconcile(): Promise<{ consistent: boolean; discrepancies: unknown[] }> {
    return request('/stock/reconcile')
  },

  // --- Журнал движений (п. 4.16) ----------------------------------------------

  movements(params: Record<string, string | number | undefined>): Promise<{
    movements: Movement[]
    total: number
    page: number
    limit: number
  }> {
    const p = new URLSearchParams()
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') p.set(k, String(v))
    }
    return request(`/movements?${p}`)
  },

  async reasons(): Promise<Reason[]> {
    const res = await request<{ reasons: Reason[] | null }>('/movements/reasons')
    return res.reasons ?? []
  },

  // --- Документы --------------------------------------------------------------

  async receipts(): Promise<Receipt[]> {
    const res = await request<{ receipts: Receipt[] | null }>('/receipts')
    return res.receipts ?? []
  },
  createReceipt(body: Record<string, unknown>): Promise<Receipt> {
    return request('/receipts', { method: 'POST', body })
  },
  receipt(id: number): Promise<Receipt> {
    return request(`/receipts/${id}`)
  },
  setReceiptLine(
    id: number,
    body: { product_id: number; quantity: number; accumulate?: boolean },
  ): Promise<Receipt> {
    return request(`/receipts/${id}/lines`, { method: 'POST', body })
  },
  postReceipt(id: number): Promise<{ status: string }> {
    return request(`/receipts/${id}/post`, { method: 'POST', body: {} })
  },

  async transfers(): Promise<Transfer[]> {
    const res = await request<{ transfers: Transfer[] | null }>('/transfers')
    return res.transfers ?? []
  },
  createTransfer(body: Record<string, unknown>): Promise<Transfer> {
    return request('/transfers', { method: 'POST', body })
  },
  transfer(id: number): Promise<Transfer> {
    return request(`/transfers/${id}`)
  },
  setTransferLine(id: number, body: { product_id: number; quantity: number }): Promise<Transfer> {
    return request(`/transfers/${id}/lines`, { method: 'POST', body })
  },
  postTransfer(id: number): Promise<{ status: string }> {
    return request(`/transfers/${id}/post`, { method: 'POST', body: {} })
  },

  async inventories(): Promise<Inventory[]> {
    const res = await request<{ inventories: Inventory[] | null }>('/inventories')
    return res.inventories ?? []
  },
  createInventory(body: Record<string, unknown>): Promise<Inventory> {
    return request('/inventories', { method: 'POST', body })
  },
  inventory(id: number): Promise<Inventory> {
    return request(`/inventories/${id}`)
  },
  setInventoryLine(
    id: number,
    body: { product_id: number; actual_quantity: number | null },
  ): Promise<{ status: string }> {
    return request(`/inventories/${id}/lines`, { method: 'POST', body })
  },
  postInventory(id: number): Promise<{ status: string; movements: number }> {
    return request(`/inventories/${id}/post`, { method: 'POST', body: {} })
  },
  cancelInventory(id: number): Promise<{ status: string }> {
    return request(`/inventories/${id}/cancel`, { method: 'POST', body: {} })
  },

  /** Переключает компанию, в контексте которой работает сотрудник платформы. */
  async switchCompany(companyId: number | null): Promise<Me> {
    const me = await request<Me>('/platform/context', {
      method: 'POST',
      body: { company_id: companyId },
    })
    setCsrfToken(me.csrf_token)
    return me
  },
}
